import { imgSrc } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, Loader2, Shirt, Trash2, Upload } from "lucide-react";

export const GARMENT_TYPE_LABELS = {
  flat_lay: "Flat-lay",
  ghost_mannequin: "Ghost mannequin",
  studio_photo: "Studio photo",
};

export const GarmentLibrary = ({
  garments, selectedGarment, onSelectGarment, onDeleteGarment,
  gName, setGName, gType, setGType, gUploading, gFileRef, onUploadGarment,
}) => (
  <Card className="h-fit rounded-2xl">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-base"><Shirt className="h-4 w-4" /> Garment library</CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="space-y-2 rounded-xl border border-dashed p-3">
        <Input data-testid="garment-name-input" placeholder="Garment name (e.g. Blue Summer Dress)"
          value={gName} onChange={(e) => setGName(e.target.value)} className="rounded-xl bg-card text-sm" />
        <div className="flex gap-2">
          <Select value={gType} onValueChange={setGType}>
            <SelectTrigger data-testid="garment-type-select" className="rounded-xl bg-card text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(GARMENT_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="garment-upload-button" size="sm" variant="outline" disabled={gUploading}
            onClick={() => gFileRef.current?.click()} className="shrink-0 rounded-xl border bg-card hover:bg-muted">
            {gUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          </Button>
        </div>
        <input ref={gFileRef} data-testid="garment-file-input" type="file" accept="image/jpeg,image/png,image/webp" hidden
          onChange={(e) => onUploadGarment(e.target.files)} />
        <p className="text-[10px] text-muted-foreground">Flat-lays, ghost mannequin shots, or basic studio photos.</p>
      </div>
      {garments.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">No garments yet — upload your first product photo above.</p>
      ) : (
        <div className="grid max-h-[380px] grid-cols-2 gap-2 overflow-y-auto pr-1">
          {garments.map((g) => (
            <button key={g.id} type="button" data-testid={`garment-tile-${g.id}`}
              onClick={() => onSelectGarment(g)}
              className={`group relative overflow-hidden rounded-xl border-2 text-left transition-[border-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                selectedGarment?.id === g.id ? "border-[hsl(174,38%,34%)]" : "border-transparent hover:border-border"
              }`}>
              <img src={imgSrc(g)} alt={g.name} className="aspect-square w-full object-cover" />
              {selectedGarment?.id === g.id && (
                <span className="absolute left-1.5 top-1.5 rounded-full bg-[hsl(174,38%,34%)] p-1 text-white"><Check className="h-3 w-3" /></span>
              )}
              <span role="button" tabIndex={-1} title="Delete"
                onClick={(e) => { e.stopPropagation(); onDeleteGarment(g); }}
                className="absolute right-1.5 top-1.5 rounded-full bg-white/90 p-1 text-destructive opacity-0 transition-[opacity] group-hover:opacity-100">
                <Trash2 className="h-3 w-3" />
              </span>
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1.5 pt-5 text-[10px] font-semibold text-white">
                {g.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);
